/**
 * Compatibility facade for react-bootstrap 0.33 → 2.x.
 * Preserves legacy Panel/Well/Form APIs and bsStyle/bsSize/block props
 * while emitting enough BS3-like class names for existing LESS.
 */
import React, { useState, useEffect, Children, isValidElement } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import RBButton from 'react-bootstrap/Button';
import RBButtonGroup from 'react-bootstrap/ButtonGroup';
import RBToggleButton from 'react-bootstrap/ToggleButton';
import RBToggleButtonGroup from 'react-bootstrap/ToggleButtonGroup';
import RBModal from 'react-bootstrap/Modal';
import RBTable from 'react-bootstrap/Table';
import RBForm from 'react-bootstrap/Form';
import RBListGroup from 'react-bootstrap/ListGroup';
import RBListGroupItem from 'react-bootstrap/ListGroupItem';
import RBTabs from 'react-bootstrap/Tabs';
import RBTab from 'react-bootstrap/Tab';
import RBProgressBar from 'react-bootstrap/ProgressBar';
import RBOverlay from 'react-bootstrap/Overlay';
import RBPopover from 'react-bootstrap/Popover';
import RBImage from 'react-bootstrap/Image';
import RBAlert from 'react-bootstrap/Alert';
import RBBadge from 'react-bootstrap/Badge';
import RBInputGroup from 'react-bootstrap/InputGroup';
import RBCol from 'react-bootstrap/Col';
import RBRow from 'react-bootstrap/Row';
import RBContainer from 'react-bootstrap/Container';
import RBDropdown from 'react-bootstrap/Dropdown';
import RBDropdownButton from 'react-bootstrap/DropdownButton';
import RBCard from 'react-bootstrap/Card';
import RBAccordion from 'react-bootstrap/Accordion';

const sizeMap = {
	xsmall: 'sm',
	xs: 'sm',
	small: 'sm',
	sm: 'sm',
	large: 'lg',
	lg: 'lg'
};

const mapVariant = (bsStyle, variant) => {
	const value = variant || bsStyle || undefined;
	if (value === 'default') return 'secondary';
	return value;
};

const mapSize = (bsSize, size) => size || sizeMap[bsSize] || bsSize || undefined;

export const Button = React.forwardRef(function Button ({
	bsStyle,
	bsSize,
	variant,
	size,
	block,
	className,
	...rest
}, ref) {
	return (
		<RBButton
			ref={ref}
			variant={mapVariant(bsStyle, variant)}
			size={mapSize(bsSize, size)}
			className={classnames(className, { 'w-100 d-block': block })}
			{...rest}
		/>
	);
});

Button.propTypes = {
	bsStyle: PropTypes.string,
	bsSize: PropTypes.string,
	variant: PropTypes.string,
	size: PropTypes.string,
	block: PropTypes.bool,
	className: PropTypes.string
};

export function ProgressBar ({ bsStyle, variant, ...rest }) {
	return <RBProgressBar variant={mapVariant(bsStyle, variant)} {...rest} />;
}

ProgressBar.propTypes = {
	bsStyle: PropTypes.string,
	variant: PropTypes.string
};

export function Label ({ bsStyle, variant, className, ...rest }) {
	return (
		<RBBadge
			bg={mapVariant(bsStyle, variant) || 'secondary'}
			className={className}
			{...rest}
		/>
	);
}

Label.propTypes = {
	bsStyle: PropTypes.string,
	variant: PropTypes.string,
	className: PropTypes.string
};

export function FormControl ({
	componentClass,
	bsSize,
	size,
	className,
	...rest
}) {
	const as = componentClass || rest.as;
	const props = { ...rest };
	delete props.as;
	return (
		<RBForm.Control
			as={as}
			size={mapSize(bsSize, size)}
			className={className}
			{...props}
		/>
	);
}

FormControl.propTypes = {
	componentClass: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
	as: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
	bsSize: PropTypes.string,
	size: PropTypes.string,
	className: PropTypes.string
};

export function FormGroup ({ controlId, className, children, ...rest }) {
	return (
		<RBForm.Group controlId={controlId} className={classnames('mb-3', className)} {...rest}>
			{children}
		</RBForm.Group>
	);
}

FormGroup.propTypes = {
	controlId: PropTypes.string,
	className: PropTypes.string,
	children: PropTypes.node
};

export function ControlLabel ({ className, children, ...rest }) {
	return (
		<RBForm.Label className={className} {...rest}>
			{children}
		</RBForm.Label>
	);
}

ControlLabel.propTypes = {
	className: PropTypes.string,
	children: PropTypes.node
};

export function HelpBlock ({ className, children, ...rest }) {
	return (
		<RBForm.Text className={classnames('text-muted', className)} {...rest}>
			{children}
		</RBForm.Text>
	);
}

HelpBlock.propTypes = {
	className: PropTypes.string,
	children: PropTypes.node
};

export function Checkbox ({ className, children, inline, ...rest }) {
	return (
		<RBForm.Check
			type="checkbox"
			className={className}
			inline={inline}
			label={children}
			{...rest}
		/>
	);
}

Checkbox.propTypes = {
	className: PropTypes.string,
	children: PropTypes.node,
	inline: PropTypes.bool
};

export function Well ({ className, children, ...rest }) {
	return (
		<div className={classnames('well card card-body mb-3', className)} {...rest}>
			{children}
		</div>
	);
}

Well.propTypes = {
	className: PropTypes.string,
	children: PropTypes.node
};

export function Grid ({ fluid, className, children, ...rest }) {
	return (
		<RBContainer fluid={fluid !== false} className={className} {...rest}>
			{children}
		</RBContainer>
	);
}

Grid.propTypes = {
	fluid: PropTypes.bool,
	className: PropTypes.string,
	children: PropTypes.node
};

function PanelHeading ({ children, className, ...rest }) {
	return (
		<div className={classnames('panel-heading', className)} {...rest}>
			{children}
		</div>
	);
}

PanelHeading.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string
};

function PanelTitle ({ toggle, children, className, ...rest }) {
	return (
		<div className={classnames('panel-title', className)} data-panel-toggle={toggle ? 'true' : undefined} {...rest}>
			{children}
		</div>
	);
}

PanelTitle.propTypes = {
	toggle: PropTypes.bool,
	children: PropTypes.node,
	className: PropTypes.string
};

function PanelCollapse ({ children, className, ...rest }) {
	return (
		<div className={classnames('panel-collapse', className)} {...rest}>
			{children}
		</div>
	);
}

PanelCollapse.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string
};

function PanelBody ({ children, className, ...rest }) {
	return (
		<div className={classnames('panel-body', className)} {...rest}>
			{children}
		</div>
	);
}

PanelBody.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string
};

function PanelFooter ({ children, className, ...rest }) {
	return (
		<div className={classnames('panel-footer', className)} {...rest}>
			{children}
		</div>
	);
}

PanelFooter.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string
};

function extractPanelParts (children) {
	let header = null;
	let body = null;
	const extras = [];

	Children.forEach(children, (child) => {
		if (!isValidElement(child)) {
			extras.push(child);
			return;
		}
		if (child.type === PanelHeading) {
			header = child;
		} else if (child.type === PanelCollapse || child.type === PanelBody) {
			body = child.type === PanelCollapse ? child.props.children : child;
		} else if (child.type === PanelFooter) {
			extras.push(child);
		} else {
			extras.push(child);
		}
	});

	let titleNode = null;
	if (header) {
		Children.forEach(header.props.children, (child) => {
			if (isValidElement(child) && child.type === PanelTitle) {
				titleNode = child;
			}
		});
	}

	return { header, titleNode, body, extras };
}

export function Panel ({
	defaultExpanded,
	expanded,
	onToggle,
	eventKey = '0',
	bsStyle = 'default',
	className,
	children,
	...rest
}) {
	const isControlled = typeof expanded === 'boolean';
	const [activeKey, setActiveKey] = useState(
		(isControlled ? expanded : defaultExpanded) ? String(eventKey) : null
	);

	useEffect(() => {
		if (isControlled) {
			setActiveKey(expanded ? String(eventKey) : null);
		}
	}, [expanded, eventKey, isControlled]);

	const handleSelect = (key) => {
		const next = key == null ? null : String(key);
		if (!isControlled) {
			setActiveKey(next);
		}
		if (typeof onToggle === 'function') {
			onToggle(Boolean(next));
		}
	};

	const { titleNode, body, extras } = extractPanelParts(children);
	const key = String(eventKey);

	return (
		<RBAccordion
			activeKey={activeKey}
			onSelect={handleSelect}
			className={classnames('panel', `panel-${bsStyle}`, className)}
			{...rest}
		>
			<RBAccordion.Item eventKey={key} className="panel-item border-0 bg-transparent">
				<RBAccordion.Header className="panel-heading">
					<span className="panel-title">
						{titleNode ? titleNode.props.children : null}
					</span>
				</RBAccordion.Header>
				<RBAccordion.Body className="panel-body">
					{body}
					{extras}
				</RBAccordion.Body>
			</RBAccordion.Item>
		</RBAccordion>
	);
}

Panel.propTypes = {
	defaultExpanded: PropTypes.bool,
	expanded: PropTypes.bool,
	onToggle: PropTypes.func,
	eventKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	bsStyle: PropTypes.string,
	className: PropTypes.string,
	children: PropTypes.node
};

Panel.Heading = PanelHeading;
Panel.Title = PanelTitle;
Panel.Collapse = PanelCollapse;
Panel.Body = PanelBody;
Panel.Footer = PanelFooter;

function InputGroupAddon ({ children, className, ...rest }) {
	return (
		<RBInputGroup.Text className={classnames('input-group-addon', className)} {...rest}>
			{children}
		</RBInputGroup.Text>
	);
}

InputGroupAddon.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string
};

export function InputGroup ({ children, className, ...rest }) {
	return (
		<RBInputGroup className={className} {...rest}>
			{children}
		</RBInputGroup>
	);
}

InputGroup.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string
};

InputGroup.Addon = InputGroupAddon;
InputGroup.Text = RBInputGroup.Text;
InputGroup.Checkbox = RBInputGroup.Checkbox;

export function MenuItem ({ children, onClick, eventKey, className, ...rest }) {
	return (
		<RBDropdown.Item eventKey={eventKey} className={className} onClick={onClick} {...rest}>
			{children}
		</RBDropdown.Item>
	);
}

MenuItem.propTypes = {
	children: PropTypes.node,
	onClick: PropTypes.func,
	eventKey: PropTypes.any,
	className: PropTypes.string
};

export const ButtonGroup = RBButtonGroup;

/**
 * RB2 ToggleButton renders a hidden `.btn-check` input + label.
 * Without `id`/`htmlFor`, clicks on the visible button never toggle the input.
 * Auto-generate an id when callers omit one (legacy RB 0.33 API).
 */
export const ToggleButton = React.forwardRef(function ToggleButton ({ id, ...rest }, ref) {
	const autoId = React.useId();
	return <RBToggleButton ref={ref} id={id ?? autoId} {...rest} />;
});

ToggleButton.propTypes = {
	id: PropTypes.string
};

export const ToggleButtonGroup = RBToggleButtonGroup;
export const Modal = RBModal;
export const Table = RBTable;
export const ListGroup = RBListGroup;
export const ListGroupItem = RBListGroupItem;

/** RB 0.33 used `animation={false}`; RB2 drops that prop. */
export function Tabs ({ animation, ...rest }) {
	return <RBTabs {...rest} />;
}

Tabs.propTypes = {
	animation: PropTypes.bool
};

export const Tab = RBTab;
export const Overlay = RBOverlay;
export const Popover = RBPopover;
/** Map BS3 `responsive` onto BS5 `fluid`. */
export function Image ({ responsive, fluid, className, ...rest }) {
	return (
		<RBImage
			fluid={fluid || responsive}
			className={className}
			{...rest}
		/>
	);
}

Image.propTypes = {
	responsive: PropTypes.bool,
	fluid: PropTypes.bool,
	className: PropTypes.string
};
export const Alert = RBAlert;
export const Col = RBCol;
export const Row = RBRow;
export const DropdownButton = RBDropdownButton;
export const Dropdown = RBDropdown;
export const Card = RBCard;
export const Accordion = RBAccordion;
export const Form = RBForm;
export const Badge = RBBadge;

export default {
	Button,
	ButtonGroup,
	ToggleButton,
	ToggleButtonGroup,
	Modal,
	Table,
	FormControl,
	FormGroup,
	ControlLabel,
	HelpBlock,
	Checkbox,
	ListGroup,
	ListGroupItem,
	Panel,
	Well,
	Tabs,
	Tab,
	ProgressBar,
	Overlay,
	Popover,
	Image,
	Alert,
	Label,
	InputGroup,
	Col,
	Row,
	Grid,
	MenuItem,
	DropdownButton,
	Dropdown,
	Card,
	Accordion,
	Form,
	Badge
};
